/**
 * features/notifications/NotificationsPanel.tsx
 * ------------------------------------------------------------------
 * AUDIT M4 — the in-app notifications surface (collection
 * COLLECTIONS.notifications was scaffolded but never built, same
 * as Attendance was).
 *
 * A bell with an unread badge + a dropdown inbox. Subscribed by
 * recipientId (client-side sort, like Al-Nur, to avoid a composite
 * index). Mark-as-read; click a flag notification to jump to the
 * Comment Flags screen. Rendered in the Shell header.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import type { AppNotification } from '@/types';

export default function NotificationsPanel({
  onOpenFlag,
  onOpenFlaggedComment,
}: {
  onOpenFlag?: (ctx?: {
    classId?: string;
    studentId?: string;
  }) => void;
  /** Teacher clicked their own "comment flagged" notification — deep-link to
   *  the exact comment they need to review/fix. */
  onOpenFlaggedComment?: (ctx: {
    target?: string;
    classId?: string;
    subjectId?: string;
    studentId?: string;
  }) => void;
}) {
  const { userData } = useAuth();
  const [items, setItems] = useState<
    (AppNotification & { flagId?: string })[]
  >([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userData?.uid) return;
    const q = query(
      collection(db, COLLECTIONS.notifications),
      where('recipientId', '==', userData.uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<AppNotification, 'id'>),
        })) as (AppNotification & { flagId?: string })[];
        list.sort((a, b) =>
          (b.createdAt || '').localeCompare(a.createdAt || ''),
        );
        setItems(list);
      },
      () => {
        /* permission/no-data: just show nothing */
      },
    );
    return unsub;
  }, [userData?.uid]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  async function markRead(id: string) {
    try {
      await updateDoc(doc(db, COLLECTIONS.notifications, id), {
        read: true,
        readAt: new Date().toISOString(),
      });
    } catch {
      /* ignore */
    }
  }

  async function markAll() {
    try {
      const batch = writeBatch(db);
      items
        .filter((n) => !n.read)
        .forEach((n) =>
          batch.update(
            doc(db, COLLECTIONS.notifications, n.id),
            { read: true, readAt: new Date().toISOString() },
          ),
        );
      await batch.commit();
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={20} className="text-gray-600 dark:text-gray-300" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="font-bold text-sm text-gray-900 dark:text-white">
              Notifications
            </p>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-primary flex items-center gap-1 hover:underline"
              >
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 && (
            <p className="px-4 py-8 text-sm text-gray-400 text-center">
              No notifications.
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                if (!n.read) markRead(n.id);
                if (n.type === 'flag' && onOpenFlag) {
                  setOpen(false);
                  const d = (n.details ?? {}) as {
                    classId?: string;
                    studentId?: string;
                  };
                  onOpenFlag({
                    classId: d.classId,
                    studentId: d.studentId,
                  });
                } else if (typeof n.details?.link === 'string' && n.details.link.startsWith('#/')) {
                  // Generic deep link (e.g. the weekly upload reminders):
                  // the hash router in App.tsx takes it from here.
                  setOpen(false);
                  window.location.hash = n.details.link;
                } else if (n.type === 'flag_raised' && onOpenFlaggedComment) {
                  setOpen(false);
                  const d = (n.details ?? {}) as {
                    target?: string;
                    classId?: string;
                    subjectId?: string;
                    studentId?: string;
                  };
                  onOpenFlaggedComment({
                    target: d.target,
                    classId: d.classId,
                    subjectId: d.subjectId,
                    studentId: d.studentId,
                  });
                }
              }}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors ${
                n.read ? '' : 'bg-blue-50/60 dark:bg-blue-900/20'
              }`}
            >
              <div className="flex items-start gap-2">
                {!n.read && (
                  <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 shrink-0" />
                )}
                <div className={n.read ? 'pl-4' : ''}>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {n.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {n.message}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </div>
                {n.read && (
                  <Check
                    size={13}
                    className="text-gray-300 ml-auto shrink-0"
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
